import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL as API } from '../config';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        setToken(null);
        setUser(null);
    }, []);

    const fetchUser = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/auth/me`);
            // Validate that we got a real user object and not just an HTML string from SPA routing
            if (response.data && typeof response.data === 'object' && (response.data.id || response.data.email)) {
                setUser(response.data);
            } else {
                console.error('Invalid user data received from API:', response.data);
                logout();
            }
        } catch (error) {
            // Handle 403 (unverified email) — keep user session but mark as unverified
            if (error.response?.status === 403 && error.response?.data?.detail?.includes('not verified')) {
                // Decode JWT to get basic info for the verification-pending page
                try {
                    const storedToken = localStorage.getItem('token');
                    if (storedToken) {
                        const payload = JSON.parse(atob(storedToken.split('.')[1]));
                        setUser({
                            id: payload.user_id,
                            is_verified: false,
                            email: '', // Not available from JWT
                        });
                    } else {
                        logout();
                    }
                } catch {
                    logout();
                }
            } else {
                console.error('Failed to fetch user:', error);
                logout();
            }
        } finally {
            setLoading(false);
        }
    }, [logout]);

    useEffect(() => {
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            fetchUser();
        } else {
            setLoading(false);
        }
    }, [token, fetchUser]);

    const login = async (email, password) => {
        const response = await axios.post(`${API}/auth/login`, { email, password });
        const { token: newToken, user: userData } = response.data;
        localStorage.setItem('token', newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        setToken(newToken);
        setUser(userData);
        return userData;
    };

    const register = async (userData) => {
        const response = await axios.post(`${API}/auth/register`, userData);
        if (response.data.token) {
            const { token: newToken, user: newUser } = response.data;
            localStorage.setItem('token', newToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            setToken(newToken);
            setUser(newUser);
            return newUser;
        }
        return response.data;
    };

    const updateUser = async (updateData) => {
        const response = await axios.put(`${API}/auth/me`, updateData);
        setUser(response.data);
        return response.data;
    };

    return (
        <AuthContext.Provider value={{ user, token, loading, setToken, login, register, logout, updateUser }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
